import { usePermisos } from "../../context/PermisosContext";
import TableroRecursos from "../Tableros/TableroRecursos";
// src/components/Dispositivos/Dispositivos.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import CrearDispositivo from "./CrearDispositivo";
import AdministrarDispositivos from "./AdministrarDispositivos";
import AsistenteInstalacion from "./AsistenteInstalacion";

/* ================= Helpers permisos ================= */
// Devuelve true/false si el bit existe; undefined si la clave no está definida (=> no bloquea).


// Requisitos por pestaña (si un bit no existe en FEATURE_BITS, se ignora = no bloquea)
const TAB_REQUIRE = {
  tablero: ["DISPOSITIVOS_TAB_TABLERO_VIEW"],
  asistente: ["DISPOSITIVOS_TAB_ASISTENTE_VIEW"],
  administrar: ["DISPOSITIVOS_TAB_EDITAR_VIEW"],                      // ver listado/edición
  crear: ["DISPOSITIVOS_TAB_CREAR_VIEW", "DISPOSITIVOS_TAB_CREAR_EDIT"], // crear
};

const ALL_TABS = [
  { id: "tablero", label: "Tablero de dispositivos" },
  { id: "asistente", label: "Asistente opcional" },
  { id: "administrar", label: "Administrar Dispositivos" },
  { id: "crear", label: "Crear Dispositivo" },
];

/* ================= Componente ================= */
const Dispositivos = ({ initialSub, onSubChange }) => {
  const { can: hasBit } = usePermisos();
  
  const [searchParams, setSearchParams] = useSearchParams();

  // Gate global de la ventana (por robustez, la ruta ya lo protege)
  const canViewWindow = hasBit("DISPOSITIVOS_VIEW") ?? true;

  // Qué pestañas están permitidas según el bitset
  const visibleTabs = useMemo(() => {
    const allowAllIfUndefined = (bits) =>
      (bits || []).every((k) => {
        const v = hasBit(k);
        return v === true;
      });
    return ALL_TABS.filter((t) => allowAllIfUndefined(TAB_REQUIRE[t.id]));
  }, [hasBit]);

  // ----- fuente de verdad del sub actual -----
  // 1) URL ?sub=
  const subFromUrl = searchParams.get("sub") || "";
  // 2) Prop initialSub que viene del Dashboard (memoria por menú)
  // 3) Fallback: primera pestaña visible
  const computeValidTab = (candidate) => {
    if (!candidate) return visibleTabs[0]?.id || null;
    const exists = visibleTabs.some((t) => t.id === candidate);
    return exists ? candidate : visibleTabs[0]?.id || null;
  };

  const initialTab = computeValidTab(subFromUrl || initialSub || "tablero");

  const [tab, setTab] = useState(initialTab);

  // Si cambian permisos o llega otra initialSub/URL, normalizamos tab
  useEffect(() => {
    const next = computeValidTab(subFromUrl || initialSub || tab);
    if (next !== tab) setTab(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleTabs, initialSub, subFromUrl]);

  // Sincroniza estado → URL (y avisa al padre) sin loops
  useEffect(() => {
    if (!tab) return;
    const next = new URLSearchParams(searchParams);
    if (next.get("sub") !== tab) {
      next.set("sub", tab);
      setSearchParams(next, { replace: true });
    }
    onSubChange?.(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  if (!canViewWindow) {
    return (
      <div className="bg-neutral-900 text-white p-6 rounded-lg w-full min-h-[50dvh] md:min-h-screen">
        <h2 className="text-green-400 text-2xl sm:text-3xl font-bold mb-4 text-center">
          Gestión de Dispositivos
        </h2>
        <div className="bg-neutral-800 border border-neutral-700 rounded p-4 text-center">
          <p className="text-sm text-neutral-300">No tienes permiso para ver esta sección.</p>
        </div>
      </div>
    );
  }

  if (!tab || visibleTabs.length === 0) {
    return (
      <div className="bg-neutral-900 text-white p-6 rounded-lg w-full min-h-[50dvh] md:min-h-screen">
        <h2 className="text-green-400 text-2xl sm:text-3xl font-bold mb-4 text-center">
          Gestión de Dispositivos
        </h2>
        <div className="bg-neutral-800 border border-neutral-700 rounded p-4 text-center">
          <p className="text-sm text-neutral-300">
            No hay pestañas disponibles con tus permisos actuales.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-neutral-900 text-white p-2 sm:p-6 min-w-0 rounded-lg w-full min-h-[50dvh] md:min-h-screen">
      <h2 className="text-green-400 text-2xl sm:text-3xl font-bold mb-3 sm:mb-4 text-center">
        Gestión de Dispositivos
      </h2>

      {/* Tabs (solo las permitidas) */}
      <div className="mb-4 sm:mb-6 flex justify-center">
        <div className="grid grid-cols-1 sm:grid-cols-3 w-full gap-2">
          {visibleTabs.filter((t) => t.id !== "asistente").map((t) => (
            <button
              key={t.id}
              className={`w-full min-h-12 px-3 sm:px-4 py-2 rounded text-sm sm:text-base transition-colors ${
                tab === t.id ? "bg-green-600" : "bg-gray-700 hover:bg-gray-600"
              }`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 sm:mt-6">
        {tab === "tablero" && <TableroRecursos tipo="dispositivos" onOpen={setTab} />}
        {tab === "administrar" && <AdministrarDispositivos />}
        {tab === "crear" && <CrearDispositivo />}
        {tab === "asistente" && <AsistenteInstalacion key={localStorage.getItem("token")} />}
      </div>
    </div>
  );
};

export default Dispositivos;
